<template>
  <component
    :is="tag"
    ref="text"
    class="line-clamp"
    data-testid="lineClampText"
    v-bind="$attrs"
    :style="lineClampStyles"
  >
    <slot :clamped="shouldClamp" />
    <BaseStreemTooltip v-if="shouldClamp && tooltip">
      {{ tooltip }}
    </BaseStreemTooltip>
  </component>

  <slot
    :clamped="shouldClamp"
    name="after"
  />
</template>

<script lang="ts">
import { useElementSize } from "@vueuse/core";
import type { VNode } from "vue";
import { computed, defineSlots, ref, useTemplateRef, watch } from "vue";

import { BaseStreemTooltip } from "shared/components/base";
import type { Nullable } from "shared/types";

export interface LineClampProps {
  lines?: number;
  maxWidth?: string;
  showAll?: boolean;
  tag?: string;
  tooltip?: Nullable<string>;
}

export interface LineClampSlots {
  after: (props: { clamped: boolean }) => VNode[] | string;
  default: (props: { clamped: boolean }) => VNode[] | string;
}
</script>

<script lang="ts" setup>
const LINE_HEIGHT_NORMAL = 1.2;

const emit = defineEmits(["clamp-changed"]);

defineSlots<LineClampSlots>();

const props = withDefaults(defineProps<LineClampProps>(), {
  lines: 2,
  tag: "div",
  maxWidth: "100%",
  tooltip: null,
});

const text = useTemplateRef<HTMLElement>("text");
const shouldClamp = ref(false);

const { width, height } = useElementSize(text);

const lineClampStyles = computed(() => {
  const styles = {
    maxWidth: props.maxWidth,
  };

  if (!shouldClamp.value || props.showAll) {
    return styles;
  }

  return {
    ...styles,
    "-webkit-line-clamp": props.lines,
  };
});

function checkClamp(height: number, lineHeight: string) {
  const elLineCount = Math.floor(height / parseInt(lineHeight, 10));
  shouldClamp.value = elLineCount > props.lines;

  emit("clamp-changed", shouldClamp.value);
}

function handleClamp() {
  if (!text.value) return;

  // Temporarily remove line-clamp to get true height
  const originalStyles = text.value.style.cssText;
  text.value.style.cssText = "";

  const elHeight = text.value.offsetHeight;
  const styles = window.getComputedStyle(text.value);

  // Restore original styles
  text.value.style.cssText = originalStyles;

  let lineHeight: string = styles.getPropertyValue("line-height");

  if (!lineHeight.endsWith("px")) {
    const fontSize = styles.getPropertyValue("font-size").replace("px", "");
    lineHeight = (parseFloat(fontSize) * LINE_HEIGHT_NORMAL).toString();
  } else {
    lineHeight = parseFloat(lineHeight.replace("px", "")).toString();
  }

  checkClamp(elHeight, lineHeight);
}

watch(
  [width, height],
  () => {
    handleClamp();
  },
  { immediate: true }
);

defineExpose({
  checkClamp,
  handleClamp,
  shouldClamp: shouldClamp.value,
  width,
  height,
});
</script>

<style lang="scss" scoped>
.line-clamp {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
}
</style>
